import { useState } from 'react';
import { savePlantItem, deletePlantItem as delItem } from '../../api';
import LocationPickerModal from '../LocationPickerModal';

const PLANT_TYPES = [
  { value: 'CALORIFIER', label: 'Calorifier' },
  { value: 'POU', label: 'POU (Point of Use)' },
  { value: 'IPOU', label: 'IPOU (Instantaneous POU)' },
  { value: 'COMBI', label: 'Combi Boiler' },
  { value: 'COOLING_TOWER', label: 'Cooling Tower' },
  { value: 'COLD_WATER_TANK', label: 'Cold Water Tank' },
];
const typeLabel = (val) => PLANT_TYPES.find((t) => t.value === val)?.label || val;
const EMPTY = { plantType: 'CALORIFIER', itemReference: '', locationOnSite: '', riskRatingCode: '', sectionId: '' };

export default function PlantSurveyTab({ report, sections, riskRatings, onSave, readOnly = false }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState('');
  const [filterType, setFilterType] = useState('');
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  const handleChange = (e) => {
    const newForm = { ...form, [e.target.name]: e.target.value };
    if (e.target.name === 'plantType') {
      const mapping = { CALORIFIER: 'HOT_WATER_CALORIFIERS', POU: 'POU_HEATERS', IPOU: 'IPOU_HEATERS', COMBI: 'COMBI_BOILERS', COOLING_TOWER: 'COOLING_SYSTEMS', COLD_WATER_TANK: 'COLD_WATER_TANKS' };
      const sec = sections.find((s) => s.code === mapping[e.target.value]);
      if (sec) newForm.sectionId = sec.id;
    }
    setForm(newForm);
  };

  const startNew = () => {
    const sec = sections.find((s) => s.code === 'HOT_WATER_CALORIFIERS');
    setEditing('new');
    setForm({ ...EMPTY, sectionId: sec?.id || '' });
  };
  const startEdit = (item) => { setEditing(item.id); setForm({ ...item }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const handleSave = async () => {
    try {
      await savePlantItem(report.id, { ...form, id: editing === 'new' ? null : editing, sectionId: Number(form.sectionId), sortOrder: (report.plantItems || []).length });
      setEditing(null);
      setForm(EMPTY);
      setMsg('Saved');
      onSave();
      setTimeout(() => setMsg(''), 2000);
    } catch (err) { setMsg(err.message); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete this plant item?')) return; await delItem(id); onSave(); };

  const filtered = filterType ? (report.plantItems || []).filter((p) => p.plantType === filterType) : (report.plantItems || []);

  return (
    <div>
      {msg && <div className="alert alert-success">{msg}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
        {!readOnly && <button className="btn btn-primary" onClick={startNew}>Add Plant Item</button>}
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ padding: '0.4rem' }}>
          <option value="">All types</option>
          {PLANT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {!readOnly && editing && (
        <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: '1rem', marginBottom: '1rem', background: '#fafafa' }}>
          <div className="form-row">
            <div className="form-group">
              <label>Plant Type</label>
              <select name="plantType" value={form.plantType} onChange={handleChange}>
                {PLANT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Reference</label><input name="itemReference" value={form.itemReference} onChange={handleChange} placeholder="e.g. CAL01" /></div>
            <div className="form-group">
              <label>Location</label>
              <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                {form.locationOnSite ? (
                  <span style={{ flex: 1, padding: '0.5rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, fontSize: '0.85rem' }}>{form.locationOnSite}</span>
                ) : (
                  <span style={{ flex: 1, padding: '0.5rem', color: '#9ca3af', fontSize: '0.85rem' }}>No location selected</span>
                )}
                <button type="button" className="btn btn-outline" style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', whiteSpace: 'nowrap', color: '#333', borderColor: '#ccc' }} onClick={() => setLocationPickerOpen(true)}>
                  {form.locationOnSite ? 'Change' : 'Pick'}
                </button>
                {form.locationOnSite && (
                  <button type="button" className="btn btn-outline" style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem', color: '#dc2626', borderColor: '#fecaca' }} onClick={() => setForm((f) => ({ ...f, locationOnSite: '' }))}>Clear</button>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Risk Rating</label>
              <select name="riskRatingCode" value={form.riskRatingCode || ''} onChange={handleChange}>
                <option value="">—</option>
                {riskRatings.map((r) => <option key={r.code} value={r.code}>{r.code}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleSave}>Save</button>
            <button className="btn btn-outline" style={{ color: '#333', borderColor: '#ccc' }} onClick={cancel}>Cancel</button>
          </div>
        </div>
      )}

      <div className="table-scroll">
        <table className="data-table">
          <thead><tr><th>Type</th><th>Reference</th><th>Location</th><th>Risk</th>{!readOnly && <th></th>}</tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id}>
                <td>{typeLabel(p.plantType)}</td>
                <td><strong>{p.itemReference}</strong></td>
                <td>{p.locationOnSite}</td>
                <td>{p.riskRatingCode}</td>
                {!readOnly && <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button onClick={() => startEdit(p)} style={{ cursor: 'pointer', border: '1px solid #d0d5dd', background: '#fff', color: '#4361ee', fontSize: '0.8rem', padding: '5px 12px', borderRadius: 4, marginRight: 8 }}>&#9998; Edit</button>
                  <button onClick={() => handleDelete(p.id)} style={{ cursor: 'pointer', border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: '0.8rem', padding: '5px 12px', borderRadius: 4 }}>&times; Delete</button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && <p style={{ color: '#888', marginTop: '0.5rem' }}>No plant items recorded yet.</p>}

      <LocationPickerModal
        isOpen={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        onSelect={(loc) => setForm((f) => ({ ...f, locationOnSite: loc.path }))}
        siteId={report.siteId}
        organisationId={report.organisationId}
        title="Pick Location"
      />
    </div>
  );
}
